import './styles.scss'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { createChart, CrosshairMode } from 'lightweight-charts'
import { Sidebar } from 'primereact/sidebar'
import { ProgressSpinner } from 'primereact/progressspinner'
import { getLocalChartPreference, getLocalTheme, getLocalUserDetails, setLocalWithChartPreference } from 'Utils/LocalStorage_Handler'
import { commonMethods, useIsMobileView600 } from 'Utils/commonMethods'
import { WindowMinimizeIcon, WindowMaximizeIcon, GridOffIcon, GridOnIcon } from 'Assets/Icons'
import { ROUTE_STRINGS } from 'Utils/Constants'
import CustomDropdown from 'Components/CustomDropdown'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'

/* eslint-disable react/display-name */
const ForexChart = memo(({ countryData, chartLoader, setChartLoader, forexLatestData, selectedTickerData, setSelectedCurrency, isBottomPanelVisible, setIsBottomPanelVisible, selectedCurrency, formatDollarIndex }) => {
  const { t } = useTranslation()
  const chartContainerRef = useRef()
  const chartRef = useRef()
  const seriesRef = useRef()
  const timeSeriesDataRef = useRef(null)
  const currencyRef = useRef(null)
  const tooltipRef = useRef(null)
  const blinkingDotRef = useRef(null)
  const historicalDataRef = useRef(null)
  const { isMobileView, isWebView } = useIsMobileView600()

  const localChartTheme = [null, undefined].includes(getLocalChartPreference()?.chartTheme) ? getLocalTheme() === 'dark' : getLocalChartPreference()?.chartTheme
  const gridPreference = [null, undefined].includes(getLocalChartPreference()?.gridPref) ? true : getLocalChartPreference()?.gridPref
  const chartTypeData = [
    { id: 1, chartTypePref: 'Line', chartIcon: 'pi pi-chart-line' },
    { id: 2, chartTypePref: 'Candlestick', chartIcon: 'pi pi-chart-bar' }
  ]

  const { dateFormatBasedOnUser } = commonMethods
  const userDetails = getLocalUserDetails()
  const history = useHistory()
  const timeFrame = ['1D']
  const chartPreferenceRef = useRef(
    getLocalChartPreference() || {
      ticker: '',
      chartTypePref: '',
      chartTheme: '',
      gridPref: true
    }
  )

  const selectedChartType = chartTypeData?.find((option) => option?.chartTypePref === getLocalChartPreference()?.chartTypePref)

  const [durationType, setDurationType] = useState('1D')
  const [currentDate, setCurrentDate] = useState('')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showGrid, setShowGrid] = useState(userDetails ? gridPreference : true)
  const [isDarkMode, setIsDarkMode] = useState(localChartTheme)
  // const [selectedTicker, setSelectedTicker] = useState(userDetails ? getLocalChartPreference()?.ticker || countryData[0].symbolTicker5 : countryData[0].symbolTicker5)

  const [selectedTicker, setSelectedTicker] = useState(() => {
    if (!userDetails) return countryData[0].symbolTicker5

    return getLocalChartPreference()?.ticker || countryData[0].symbolTicker5
  })

  const [chartType, setChartType] = useState(() => {
    if (!userDetails) return chartTypeData[0]

    return selectedChartType ?? chartTypeData[0]
  })
  const [selectedCurrencyISO, setSelectedCurrencyISO] = useState(countryData[0])
  const [isApiError, setIsApiError] = useState({
    isError: false,
    message: ''
  })
  const [latestDataPoint, setLatestDataPoint] = useState(null)
  const [getCurrentPrice, setGetCurrentPrice] = useState({})

  const chartTypeRef = useRef(chartType?.chartTypePref)

  useEffect(() => {
    chartTypeRef.current = chartType?.chartTypePref

    if (timeSeriesDataRef.current) {
      initializeChart()
    }
  }, [chartType, isFullscreen])

  // Commented for future-ref: Current UTC Time
  // function getUTCDateTimeISO() {
  //   const now = new Date()

  //   const year = now.getUTCFullYear()
  //   const month = String(now.getUTCMonth() + 1).padStart(2, '0')
  //   const day = String(now.getUTCDate()).padStart(2, '0')

  //   return `${year}-${month}-${day}T00:00:00`
  // }

  useEffect(() => {
    const interval = setInterval(() => {
      if (!timeSeriesDataRef.current || !seriesRef.current) return

      const lastPoint = timeSeriesDataRef.current[timeSeriesDataRef.current.length - 1]
      const nextValue = {
        ...lastPoint,
        time: lastPoint.dataDTM.split('T')[0],
        value: lastPoint.close
      }

      setLatestDataPoint(nextValue)
    }, 700)

    return () => clearInterval(interval)
  }, [])

  // This effect runs the animation loop based on latestDataPoint changes
  useEffect(() => {
    let animationFrameId

    const animate = () => {
      updateBlinkingDotPosition()
      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    return () => cancelAnimationFrame(animationFrameId)
  }, [latestDataPoint])

  // Blink interval toggling visibility every second
  useEffect(() => {
    const interval = setInterval(() => {
      if (!blinkingDotRef.current) return

      blinkingDotRef.current.style.display = blinkingDotRef.current.style.display === 'block' ? 'none' : 'block'
    }, 700)

    return () => clearInterval(interval)
  }, [])

  const updateBlinkingDotPosition = () => {
    const lastPoint = latestDataPoint

    if (!lastPoint || !blinkingDotRef.current || !chartRef.current || !seriesRef.current) return

    const priceCoordinate = seriesRef.current.priceToCoordinate(lastPoint.value || lastPoint.close)
    const timeCoordinate = chartRef.current.timeScale().timeToCoordinate(lastPoint.time)

    if (priceCoordinate !== null && timeCoordinate !== null) {
      blinkingDotRef.current.style.left = `${Math.round(timeCoordinate) - 5}px`
      blinkingDotRef.current.style.top = `${Math.round(priceCoordinate) - 5}px`
    }
  }

  const initializeChart = (dark) => {
    if (!chartContainerRef.current) return

    // Kept it for future reference x and y axis space
    // let visibleRange = null

    if (chartRef.current) {
      // visibleRange = chartRef.current.timeScale().getVisibleRange()

      chartRef.current.timeScale().applyOptions({
        rightOffset: 8
      })
    }

    cleanUpChart()

    const chartOptions = {
      width: chartContainerRef.current.clientWidth,

      // commented for the area series(for future reference)
      // height: chartContainerRef.current.clientHeight,
      height: isFullscreen ? window.innerHeight : chartContainerRef.current.clientHeight,
      layout: {
        background: { color: dark || isDarkMode ? '#253248' : '#ffffff' },
        textColor: dark || isDarkMode ? '#DDD' : '#000'
      },

      grid: {
        /* eslint-disable-next-line no-nested-ternary */
        vertLines: { color: showGrid ? (dark || isDarkMode ? '#1a2538' : '#e1ecf2') : '#ffffff00' },
        /* eslint-disable-next-line no-nested-ternary */
        horzLines: { color: showGrid ? (dark || isDarkMode ? '#1a2538' : '#e1ecf2') : '#ffffff00' }
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#75869655' },
        horzLine: { color: '#75869655' }
      },
      timeScale: {
        timeVisible: false,
        secondsVisible: false,
        rightOffset: 8
      },
      priceScale: { borderColor: '#cccccc' },
      handleScale: { axisPressedMouseMove: true }
    }

    const candleStyle = {
      upColor: '#4CAF50',
      downColor: '#F44336',
      borderUpColor: '#4CAF50',
      borderDownColor: '#F44336',
      wickUpColor: '#4CAF50',
      wickDownColor: '#F44336'
    }

    const lineSeries = {
      color: '#50b3f6',
      lineWidth: 2
    }

    chartRef.current = createChart(chartContainerRef.current, chartOptions)
    // commented for the area series(for future reference)
    // const colorConfig = getChartLineColor(timeSeriesDataRef?.current?.[timeSeriesDataRef.current.length - 1]?.netChg)

    if (chartTypeRef.current === 'Candlestick') {
      seriesRef.current = chartRef.current.addCandlestickSeries(candleStyle)
    } else {
      seriesRef.current = chartRef.current.addLineSeries(lineSeries)
      // commented for the area series(for future reference)
      // seriesRef.current = chartRef.current.addAreaSeries({
      //   topColor: colorConfig.topColor,
      //   bottomColor: colorConfig.bottomColor,
      //   lineColor: colorConfig.lineColor,
      //   lineWidth: 2
      // })
    }

    updateChart()

    // Kept it for future reference x and y axis space
    // if (visibleRange) {
    //   chartRef.current.timeScale().setVisibleRange(visibleRange)

    //   chartRef.current.timeScale().applyOptions({
    //     rightOffset: 8
    //   })
    // }
  }

  // This useEffect used for the rotating landscape and portrait
  useEffect(() => {
    let resizeTimeout

    const handleResizeOrRotate = () => {
      clearTimeout(resizeTimeout)

      resizeTimeout = setTimeout(() => {
        let orientationType = window.screen.orientation?.type

        if (!orientationType) {
          orientationType = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
        }

        if (chartContainerRef.current && timeSeriesDataRef.current?.length > 0) {
          initializeChart()
        }
      }, 100)
    }

    window.addEventListener('resize', handleResizeOrRotate)
    window.addEventListener('orientationchange', handleResizeOrRotate)

    return () => {
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', handleResizeOrRotate)
      window.removeEventListener('orientationchange', handleResizeOrRotate)
    }
  }, [])

  useEffect(() => {
    const selected = countryData.find((currencySelected) => currencySelected.symbolTicker5 === selectedTicker)

    getCurrencyHistoryData(selectedTicker, selected)

    setSelectedCurrency(selected?.targetCurrency)

    const updateTheme = () => {
      chartPreferenceRef.current.chartTheme = getLocalTheme() === 'dark'
      setLocalWithChartPreference(chartPreferenceRef.current)
      setIsDarkMode(getLocalTheme() === 'dark')
      initializeChart(getLocalTheme() === 'dark')
    }

    window.addEventListener('themeChanged', updateTheme)

    return () => {
      window.removeEventListener('themeChanged', updateTheme)
      cleanUpChart()
    }
  }, [])

  useEffect(() => {
    if (selectedTickerData) {
      setSelectedTicker(selectedTickerData)

      const selected = countryData.find((currencySelected) => currencySelected?.symbolTicker5 === selectedTickerData)

      setGetCurrentPrice(selected)

      getCurrencyHistoryData(selectedTickerData, selected)

      const updateTheme = () => {
        chartPreferenceRef.current.chartTheme = getLocalTheme() === 'dark'
        setLocalWithChartPreference(chartPreferenceRef.current)
        setIsDarkMode(getLocalTheme() === 'dark')
        initializeChart(getLocalTheme() === 'dark')
      }

      window.addEventListener('themeChanged', updateTheme)

      return () => {
        window.removeEventListener('themeChanged', updateTheme)
        cleanUpChart()
      }
    }
  }, [selectedTickerData])

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.applyOptions({
        layout: {
          background: { color: isDarkMode ? '#253248' : '#ffffff' },
          textColor: isDarkMode ? '#DDD' : '#000'
        },

        grid: {
          /* eslint-disable-next-line no-nested-ternary */
          vertLines: { color: showGrid ? (isDarkMode ? '#1a2538' : '#e1ecf2') : '#ffffff00' },
          /* eslint-disable-next-line no-nested-ternary */
          horzLines: { color: showGrid ? (isDarkMode ? '#1a2538' : '#e1ecf2') : '#ffffff00' }
        }
      })
    }
  }, [isDarkMode, showGrid])

  const cleanUpChart = () => {
    if (chartRef.current) {
      chartRef.current.remove()
      chartRef.current = null
      seriesRef.current = null
    }
  }

  // commented for the area series(for future reference)
  // const getChartLineColor = (value) => {
  //   const lineColorCode = Number(value) > 0 ? '#4CAF50' : '#FF1100'
  //   const topColorCode = Number(value) > 0 ? 'rgba(76, 175, 80, 0.4)' : 'rgba(255, 17, 0, 0.4)'
  //   const bottomColorCode = Number(value) > 0 ? 'rgba(76, 175, 80, 0.05)' : 'rgba(255, 17, 0, 0.05)'

  //   return {
  //     lineColor: Number(value) === 0 ? '#50b3f6' : lineColorCode,
  //     topColor: Number(value) === 0 ? 'rgba(80, 179, 246, 0.4)' : topColorCode,
  //     bottomColor: Number(value) === 0 ? 'rgba(80, 179, 246, 0.0)' : bottomColorCode
  //   }
  // }

  useEffect(() => {
    let timer

    clearTimeout(timer)
    if (forexLatestData?.RowIdentity && timeSeriesDataRef?.current && forexLatestData?.symbolTicker5 === selectedTicker && seriesRef?.current) {
      timer = setTimeout(() => {
        const update = (existingData, currentData) => {
          const updatedList = [...existingData]

          const index = existingData?.findIndex((ele) => ele.dataDTM === currentData?.tradeDate18)

          const lastHistoricalCurrencyData = historicalDataRef.current[historicalDataRef.current.length - 1]

          if (index !== -1) {
            updatedList[index] = {
              id: currentData?.RowIdentity,
              dataDTM: currentData?.tradeDate18,
              open: lastHistoricalCurrencyData.closeRounded,
              high: forexLatestData?.high || 'NA',
              low: forexLatestData?.low || 'NA',
              close: currentData?.tradePrice8,
              preClose: lastHistoricalCurrencyData.closeRounded,
              netChg: 0.22,
              pctChg: '0.26%',
              todayPrice: currentData?.todayPrice
            }
          } else {
            updatedList.push({
              id: currentData?.RowIdentity,
              dataDTM: currentData?.tradeDate18,
              open: lastHistoricalCurrencyData.closeRounded,
              high: forexLatestData?.high || 'NA',
              low: forexLatestData?.low || 'NA',
              close: currentData?.tradePrice8,
              preClose: lastHistoricalCurrencyData.closeRounded,
              netChg: 0.22,
              pctChg: '0.26%',
              todayPrice: currentData?.todayPrice
            })
          }

          return updatedList
        }

        const updatedWithLiveData = update(timeSeriesDataRef.current, forexLatestData)

        timeSeriesDataRef.current = updatedWithLiveData

        const sortedData = timeSeriesDataRef.current.sort((a, b) => new Date(a.dataDTM) - new Date(b.dataDTM))
        let formatted = []

        if (chartTypeRef.current === 'Candlestick') {
          formatted = sortedData.map((item) => ({
            time: item.dataDTM.split('T')[0],
            open: Number(item.open),
            high: Number(item.high),
            low: Number(item.low),
            close: Number(item.close)
          }))
        } else {
          formatted = sortedData.map((item) => ({
            time: item.dataDTM.split('T')[0],
            value: Number(item.close),
            percentageChange: item?.percentage_change
          }))
        }

        seriesRef?.current?.setData(formatted)
      }, 200)
    }

    return () => {
      clearTimeout(timer)
    }
  }, [forexLatestData])

  const getCurrencyHistoryData = async (symbolTicker, selected) => {
    setChartLoader(true)
    setIsApiError({
      isError: false,
      message: ''
    })

    try {
      const response = await apiAdapterCoffeeWeb.getForexDaysData({ symbolTicker })

      if (!response?.data?.returnLst?.[0] || response?.data.returnLst?.[0]?.length === 0) {
        currencyRef.current = {
          targetCurrency: '$ INDEX'
        }

        setIsApiError({
          isError: true,
          message: `Data for the selected currency ${selected?.baseCurrency || ''}${selected?.baseCurrency ? '/' : ''}${selected?.targetCurrency || selectedCurrencyISO?.targetCurrency || ' '} is currently unavailable.`
        })
      } else {
        const forexResponse = response?.data?.returnLst?.[0]

        const forexResponseCurrency = forexResponse.currency
        const forexResponseBaseCurrency = forexResponse.baseCurrency
        const forexResponseTargetCurrency = forexResponse.targetCurrency

        currencyRef.current = {
          baseCurrency: forexResponseBaseCurrency,
          targetCurrency: forexResponseTargetCurrency
        }

        if (Object.keys(forexResponse).length !== 0) {
          const sortedData = [...forexResponseCurrency]?.sort((a, b) => new Date(a.dataDTM) - new Date(b.dataDTM))

          historicalDataRef.current = sortedData
          timeSeriesDataRef.current = sortedData
          initializeChart()
        }
      }
    } catch (error) {
      console.log(error)
      setIsApiError({
        isError: true,
        message: 'Unable to load data. Please try again later.'
      })
    } finally {
      setChartLoader(false)
    }
  }

  const updateChart = () => {
    const rawData = timeSeriesDataRef?.current

    let formatted = []

    if (rawData && Array.isArray(rawData)) {
      const sortedData = rawData.sort((a, b) => new Date(a.dataDTM) - new Date(b.dataDTM))

      if (chartTypeRef.current === 'Candlestick') {
        formatted = sortedData.map((item) => ({
          time: item.dataDTM.split('T')[0],
          open: Number(item.open),
          high: Number(item.high),
          low: Number(item.low),
          close: Number(item.close)
        }))
      } else {
        formatted = sortedData.map((item) => ({
          time: item.dataDTM.split('T')[0],
          value: Number(item.close),
          percentageChange: item?.percentage_change
        }))
      }
    }

    seriesRef.current.setData(formatted)

    const lastPoint = formatted[formatted.length - 1]

    setLatestDataPoint(lastPoint)

    chartRef.current.timeScale().fitContent()

    const toolTip = tooltipRef.current

    chartRef.current.subscribeCrosshairMove((param) => {
      if (!param || !param.time) {
        toolTip.style.display = 'none'

        return
      }

      const { point } = param

      const price = timeSeriesDataRef?.current.find((item) => item.dataDTM.split('T')[0] === param.time)

      if (!price) {
        toolTip.style.display = 'none'
      }

      const dateStr = new Date(param.time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()
      // eslint-disable-next-line prefer-const
      let [day, month, year] = dateStr.split(' ')

      if (month === 'SEPT') {
        month = 'SEP'
      }

      const formattedDate = `${day}-${month}-${year}`

      setCurrentDate(formattedDate)
      /* eslint-disable-next-line no-nested-ternary */
      const highColor = Number(price?.high) > Number(price?.preClose) ? 'high-value-green' : Number(price?.high) < Number(price?.preClose) ? 'low-value-red' : ''
      /* eslint-disable-next-line no-nested-ternary */
      const lowColor = Number(price?.low) > Number(price?.preClose) ? 'high-value-green' : Number(price?.low) < Number(price?.preClose) ? 'low-value-red' : ''

      // Commented for Last 3 data in Tooltip
      //     const netChngColor = Number(price?.netChg) > 0 ? 'high-value-green' : 'low-value-red'
      //     <div class="tooltip-item"><span class="labeling">Pre Close</span> <span>:</span> <span id="value-field">${parseFloat(price?.preClose)}</span></div>
      //     <div class="tooltip-item"><span class="labeling">Net Chg</span> <span>:</span> <span id="value-field" class="${netChngColor}">${price?.netChg >= 0 ? '+' : ''}${parseFloat(price?.netChg)}</span></div>
      //     <div class="tooltip-item"><span class="labeling">Pct Chg</span> <span>:</span> <span id="value-field" class="${netChngColor}">${price?.netChg >= 0 ? '+' : ''}${parseFloat(price?.pctChg.split('%')[0])}%</span></div>

      const dynamicContent = toolTip.querySelector('#tooltip-dynamic-content')

      dynamicContent.innerHTML = `
  <div class="tooltip-info ${!userDetails && 'tooltip-filter-bg'}">
  ${price?.is52WeekHigh ? `<div class="tooltip-item"><span class="labeling">52 Weeks High</span> <span>:</span> <span id="value-field" class="high-value-green">${price?.high}</span></div>` : ''}
    <div class="tooltip-item"><span class="labeling">Open</span> <span>:</span> <span id="value-field">${price?.openRounded || price?.open}</span></div>
    <div class="tooltip-item"><span class="labeling">High</span> <span>:</span> <span class="${highColor}" id="value-field">${price?.highRounded || price?.high}</span></div>
    <div class="tooltip-item"><span class="labeling">Low</span> <span>:</span> <span class="${lowColor}" id="value-field">${price?.lowRounded || price?.low}</span></div>
    <div class="tooltip-item"><span class="labeling">${price?.todayPrice ? 'Last' : 'Close'}</span> <span>:</span> <span id="value-field">${price?.closeRounded || price?.close}</span></div>
     ${price?.is52WeekLow ? `<div class="tooltip-item"><span class="labeling">52 Weeks Low</span> <span>:</span> <span id="value-field" class="low-value-red">${price?.low}</span></div>` : ''}
    </div>
    ${!userDetails ? `<div class="create-account-message"><div class="message">${t('TO_ACCESS_MORE_FEATURE_CREATE_ACCOUNT')}</div></div>` : ''}
    </div>
`

      toolTip.style.display = 'block'
      const chartRect = chartContainerRef.current.getBoundingClientRect()
      const toolTipWidth = toolTip.getBoundingClientRect().width
      const toolTipHeight = toolTip.getBoundingClientRect().height

      const leftOffset = 30
      let left

      if (point.x < toolTipWidth + 270) {
        left = point.x + leftOffset
      } else if (point.x > chartRect.width - toolTipWidth - 270) {
        left = point.x - toolTipWidth - leftOffset
      } else {
        left = point.x - toolTipWidth - leftOffset
      }

      toolTip.style.left = `${Math.max(0, Math.min(chartRect.width - toolTipWidth, left))}px`
      toolTip.style.top = `${Math.max(0, Math.min(chartRect.height - toolTipHeight, point.y - 140))}px`
    })
  }

  const handleTimeFrameChange = (type) => {
    setDurationType(() => type)
    updateChart(type)
  }

  const handleSelectCurrency = (event) => {
    const newTicker = event.target.value
    const selected = countryData.find((currencySelected) => currencySelected.symbolTicker5 === newTicker)

    setSelectedCurrency(selected?.targetCurrency)
    setSelectedCurrencyISO(selected)
    cleanUpChart()

    if (userDetails) {
      chartPreferenceRef.current.ticker = newTicker
      setLocalWithChartPreference(chartPreferenceRef.current)
    }

    setSelectedTicker(newTicker)
    getCurrencyHistoryData(newTicker, selected)
  }

  const dataColor = (percentageChangeRaw) => {
    // const [percentageChange, _] = percentageChangeRaw.split('%') // Commented if used based on response of API

    if (Number(percentageChangeRaw) === 0) return ''

    return Number(percentageChangeRaw) > 0 ? 'pos' : 'neg'
  }

  const toggleView = () => {
    if (!userDetails) {
      history.push(ROUTE_STRINGS.login)
    } else {
      setIsFullscreen(!isFullscreen)
    }
  }

  const { isError, message } = isApiError

  const toggleSwapOption = () => {
    const newType = chartType?.chartTypePref === 'Line' ? 'Candlestick' : 'Line'
    const selectedType = chartTypeData?.find((type) => type.chartTypePref === newType)

    chartPreferenceRef.current.chartTypePref = selectedType?.chartTypePref
    setLocalWithChartPreference(chartPreferenceRef?.current)
    setChartType(selectedType)
  }

  const bottomPanelHeader = (
    <div className="selected-currency-info">
      <img src="https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/USA.svg" alt="country-flag" className="currency-flag-image" />
      <span className="usd-currency" style={{ color: !isDarkMode ? '#252525' : '#fff' }}>
        {t('One_USD')}
      </span>
      <img src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${currencyRef?.current?.targetCurrency === '$ INDEX' ? 'USA' : currencyRef?.current?.targetCurrency?.countryISO3 || ''}.svg`} alt="country-flag" className="currency-flag-image" />
      {/* eslint-disable-next-line  */}
      <span style={{ color: Number(getCurrentPrice.pctChg362) === 0 ? (isDarkMode ? '#fff' : '#252525') : undefined }} className={`currency-label ${Number(getCurrentPrice.pctChg362) > 0 ? 'positive-currency-change' : Number(getCurrentPrice.pctChg362) < 0 ? 'negative-currency-change' : 'neutral-currency-change'}`}>
        {currencyRef?.current?.targetCurrency ? `${getCurrentPrice.tradePrice8} ${getCurrentPrice.targetCurrency} (${getCurrentPrice.pctChg362})` : t('LOADING')}
      </span>
    </div>
  )

  const UIforForexChart = ({ isMobile = false }) => {
    const selectedCountryData = countryData.find((currencySelected) => currencySelected.targetCurrency === selectedCurrency)

    const showFullscreenToggle = !isMobile

    return (
      <>
        {countryData?.length > 0 && (
          <div className={`forex-chart-container ${isFullscreen ? 'fullscreen' : ''}`} data-testid="forex-chart-container">
            <div className="forex-chart-wrapper" style={{ backgroundColor: isDarkMode ? '#252525' : '#fff' }}>
              <div className="selection-options">
                <div className="selection-sub-option">
                  <div className="date-selector" data-testid="date-selector">
                    {timeFrame.map((ele, index) => (
                      <button data-testid="button-time-frame-change" key={index} className={ele === durationType ? 'active-nav' : ''} onClick={ele !== durationType ? () => handleTimeFrameChange(ele) : null}>
                        {ele}
                      </button>
                    ))}
                  </div>
                  <div
                    className="grid-on-off-container"
                    onClick={() => {
                      setShowGrid(!showGrid)
                      if (userDetails) {
                        chartPreferenceRef.current.gridPref = !showGrid
                        setLocalWithChartPreference(chartPreferenceRef.current)
                      }
                    }}
                  >
                    {showGrid ? <GridOnIcon /> : <GridOffIcon />}
                  </div>

                  <div
                    className="darkmode-toggle-button"
                    onClick={() => {
                      setIsDarkMode(!isDarkMode)
                      chartPreferenceRef.current.chartTheme = !isDarkMode
                      setLocalWithChartPreference(chartPreferenceRef.current)
                    }}
                    data-testid="dark-mode-toggle-button"
                  >
                    {isDarkMode ? <span className="pi pi-sun" data-testid="light-mode-icon" /> : <span className="pi pi-moon" data-testid="dark-mode-icon" />}
                  </div>
                  {showFullscreenToggle && (
                    <div className="fullscreen-toggle" onClick={() => toggleView()} data-testid="fullscreen-toggle">
                      {isFullscreen ? <img className="window-minimize-icon" src={WindowMinimizeIcon} alt="WindowMinimizeIcon" /> : <img className="window-maximize-icon" src={WindowMaximizeIcon} alt="WindowMinimizeIcon" data-testid="window-maximize-icon" />}
                    </div>
                  )}

                  {showFullscreenToggle && (
                    <div className="currency-data">
                      <div data-testid="select-chart-type">
                        {/* <select value={currencyType.symbolTicker5} onChange={handleSelectCurrency} data-testid="select-chart-type">
                      {countryData.map((el, index) => (
                        <option key={index} value={el.symbolTicker5} data-testid="candle-stick-option">
                          {el.baseCurrency}/{el.targetCurrency}
                        </option>
                      ))}
                    </select> */}

                        {countryData?.length > 0 ? (
                          <CustomDropdown
                            value={selectedTicker}
                            options={countryData?.map(({ baseCurrency, targetCurrency, symbolTicker5 }) => ({
                              label: `${baseCurrency}${baseCurrency && '/'}${formatDollarIndex(targetCurrency)}`,
                              value: symbolTicker5
                            }))}
                            onChange={(event) => {
                              handleSelectCurrency(event)
                            }}
                            showFlags={false}
                            filter={true}
                            placeholder={t('SELECT_CURRENCY')}
                            customThemeDropdown="currency-selector-dropdown"
                            customThemePanel="currency-chart-options-panel"
                            disabled={chartLoader}
                            testId="currency-dropdown-option"
                            isThemeApplied={false}
                          />
                        ) : (
                          <div className="dropdown-loader">Loading...</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="chart-type-dropdown">
                    <div className="chart-swap-button" onClick={toggleSwapOption}>
                      <span>{chartType?.chartTypePref}</span>

                      <div className="swap-icon" data-testid="icon-container">
                        <i className="pi pi-angle-up upper-arrow" />
                        <i className="pi pi-angle-down down-arrow" />
                      </div>
                    </div>
                  </div>
                </div>
                {showFullscreenToggle && (
                  <div className="selected-currency-info" data-testid="selected-currency-info">
                    <img src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${selectedCountryData?.targetCountryIso3}.svg`} alt="country-flag" className="currency-flag-image" />
                    <span className="currency-label" data-testid="currency-label">
                      {selectedCountryData ? `${formatDollarIndex(selectedCountryData.targetCurrency)} ${selectedCountryData.targetCountryName ? ` - ${selectedCountryData.targetCountryName.toUpperCase()}` : ''}` : t('LOADING')}
                    </span>
                  </div>
                )}
              </div>
              {!isError && (
                <div id="tooltip" className={`tooltips ${isDarkMode ? 'dark-theme-tooltip' : 'light-theme-tooltip'} ${userDetails ? '' : 'guest'}`} ref={tooltipRef} data-testid="tooltip-container">
                  <div className="date-format">{dateFormatBasedOnUser(currentDate)}</div>
                  <div className="flag-container">
                    <div className="country-type" data-testid="base-currency-type">
                      {/* <img src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${currencyRef?.current?.baseCurrency?.countryISO3}.svg`} alt="Base Flag" /> */}
                      <div>{currencyRef?.current?.baseCurrency?.currencyCode}</div>
                    </div>
                    <div>v/s</div>
                    <div className="country-type" data-testid="target-currency-type">
                      {/* <img src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${currencyRef?.current?.targetCurrency?.countryISO3}.svg`} alt="Target Flag" /> */}
                      <div>{currencyRef?.current?.targetCurrency?.currencyCode}</div>
                    </div>
                  </div>
                  <div className="tooltip-content" id="tooltip-dynamic-content" />
                </div>
              )}
              {chartLoader && (
                <div className="forex-loader-container" data-testid="forex-loader-container">
                  <ProgressSpinner className="progress-spinner" strokeWidth="4" animationDuration="2s" />
                  <p>Loading...</p>
                </div>
              )}
              {isError && (
                <div className="error-message-div">
                  <i className="pi pi-times-circle"></i>
                  <p className="forex-error-message" style={{ color: isDarkMode ? 'white' : 'black' }}>
                    {message}
                  </p>
                </div>
              )}

              <div ref={chartContainerRef} style={{ width: '100%', height: '100%', position: 'relative' }} data-testid="forex-chart" />
              {chartType?.chartTypePref === 'Line' && !isApiError.isError && !chartLoader && <div ref={blinkingDotRef} className="blinking-dot" data-testid="blinking-dot"></div>}
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {isWebView && UIforForexChart({ isMobile: false })}

      {isMobileView && (
        <Sidebar header={bottomPanelHeader} position="bottom" visible={isBottomPanelVisible} onHide={() => setIsBottomPanelVisible(false)} showCloseIcon={true} className="custom-bottom-panel" style={{ backgroundColor: isDarkMode ? '#2d2d2d' : '#fff' }}>
          {countryData?.length > 0 && UIforForexChart({ isMobile: true })}
        </Sidebar>
      )}
    </>
  )
})

export default ForexChart
